import { useEffect, useMemo } from 'react'

import {
  ProjectFields,
  YesObject,
  useDisplayUserError
} from '@yes.technology/react-toolkit'
import { useTranslation } from 'react-i18n-lite'
import useFetchYesObjects from './useFetchYesObjects'

interface QueryCondition {
  [key: string]: {
    [operator: string]: string
  }
}

type UseFetchYesObjectsArgs = {
  objectUUID?: string
  objectSID?: string
  objectclassUUID: string
  showLoading?: boolean
  skipRelations?: boolean
  skipMetadata?: boolean
  projectFields?: ProjectFields
  displayMessageOnNoItemsFound?: boolean
  enabled?: boolean
  hasActiveSitutation?: boolean
  refetchInterval?: number
}

export default function useYesObject<YesObjectType extends YesObject>({
  objectUUID,
  objectSID,
  objectclassUUID,
  showLoading = false,
  skipRelations = false,
  skipMetadata = false,
  projectFields,
  displayMessageOnNoItemsFound = true,
  enabled = true,
  hasActiveSitutation = false,
  refetchInterval
}: UseFetchYesObjectsArgs) {
  const { t } = useTranslation()
  const { displayUserError } = useDisplayUserError()

  const filterBody = useMemo(() => {
    const queryConditions: QueryCondition[] = []

    if (hasActiveSitutation) {
      queryConditions.push({
        'situation.des': {
          $eq: window.activeSituationCode
        }
      })
    }

    if (objectUUID) {
      queryConditions.push({
        'uuid.des': { $eq: objectUUID }
      })
    }

    if (objectSID) {
      queryConditions.push({
        'sid.des': { $eq: objectSID }
      })
    }

    return {
      query: {
        $and: queryConditions
      },
      objectclass: objectclassUUID,
      skip_relations: skipRelations,
      skip_metadata: skipMetadata,
      project_fields: projectFields
    }
  }, [
    objectUUID,
    objectSID,
    objectclassUUID,
    projectFields,
    skipMetadata,
    skipRelations,
    hasActiveSitutation
  ])

  const {
    yesObjects,
    isLoading,
    fetchYesObjects,
    pagination,
    isFetched,
    isFetching,
    error
  } = useFetchYesObjects({
    filterBody,
    showLoading,
    enabled: enabled && !!objectclassUUID && (!!objectUUID || !!objectSID),
    initialPagination: { limit: 1, offset: 0 },
    refetchInterval
  })

  useEffect(() => {
    if (isFetched && !yesObjects.length && displayMessageOnNoItemsFound) {
      displayUserError({
        error,
        userMessageFallback: t('error-message.object-not-found')
      })
    }
  }, [
    isFetched,
    yesObjects,
    displayMessageOnNoItemsFound,
    t,
    displayUserError,
    error
  ])

  return {
    yesObject: yesObjects[0] as YesObjectType | undefined,
    isLoading,
    isFetching,
    isFetched,
    pagination,
    fetchObjects: fetchYesObjects
  }
}
